/**
 * Normalises a raw `twoFactorDeferralCount` into a plain integer.
 *
 * The count reaches the client as a tri-state. `User.TwoFactorDeferralCount` is a
 * nullable column, new users are created without it, and the login response always
 * carries the key -- so callers see `null`, `0`, or a number (and, via JSON, possibly
 * a numeric string). NULL and 0 both mean the same thing to the domain: this user has
 * never deferred the 2FA prompt.
 *
 * Reading the raw field invites every caller to invent its own falsy check, which is
 * how TECH-11659 happened: a guard meant for the logout race (`!authenticatedUser`)
 * also swallowed a count of `null`, blanking the modal's button label. Normalise once,
 * here, so every reader gets a number it can compare against.
 *
 * @param {number|string|null|undefined} value
 * @returns {number} the deferral count, with any absent or unparseable value as 0
 */
export function twoFactorDeferralsFrom(value) {
  const count = Number(value);

  return Number.isFinite(count) ? count : 0;
}

/**
 * The deferral count at which the user gets one last 2FA prompt before the app
 * stops asking. Mirrors NEVER_ASK_FOR_2FA_AGAIN - 1 in LoginUserApiRequest.cs.
 */
export const FINAL_PROMPT_DEFERRAL_COUNT = 5;

/**
 * Whether the 2FA setup modal should be shown to this user.
 *
 * Prompt while they have deferred at most once, then go quiet, then make one final
 * ask at {@link FINAL_PROMPT_DEFERRAL_COUNT} unless they have explicitly declined SMS.
 *
 * The advisor and client portals each carried their own copy of this rule and had
 * already drifted apart; both also tested `twoFactorDeferralCount == null`, which
 * matches null and undefined but not 0, so a user sitting at 0 was silently exempt
 * from the prompt entirely (TECH-13257). Normalising the count first collapses that
 * hole: 0 and NULL both mean "has never deferred" and both prompt.
 *
 * @param {object|null|undefined} user the authenticated user
 * @returns {boolean}
 */
export function shouldPromptTwoFactorSetup(user) {
  if (!user || user.twoFactorEnabled) return false;

  const deferrals = twoFactorDeferralsFrom(user.twoFactorDeferralCount);

  if (deferrals <= 1) return true;

  return (
    deferrals === FINAL_PROMPT_DEFERRAL_COUNT && !user.smsTwoFactorDeclined
  );
}
